/**
 * useWorkspaceCreation - Custom hook for workspace creation logic
 * Extracted from AppKeywordResearchPage to centralize workspace creation logic
 */

import { useState, useCallback } from 'react';
import { useWorkspaceCreation as useWorkspaceCreationBase } from '@/modules/app/hooks/useWorkspace';
import { generateWorkspaceName } from '@/modules/app/utils/workspaceNaming';
import { debugLog } from '@/lib/debugUtils';
import { useAuth } from '@/contexts/AuthContext';

interface WorkspaceCreationState {
  isCreatingWorkspace: boolean;
  workspaceCreated?: {
    id: string;
    name: string;
  };
}

interface UseWorkspaceCreationForSearchResult {
  createWorkspaceForSearch: (keyword: string, languageName: string, countryName: string) => Promise<{
    workspaceId: string;
    workspaceName: string;
  }>;
  workspaceState: WorkspaceCreationState;
  resetWorkspaceState: () => void;
  isCreatingWorkspace: boolean;
}

/**
 * Hook for handling workspace creation during keyword search
 */
export function useWorkspaceCreationForSearch(): UseWorkspaceCreationForSearchResult {
  const [workspaceState, setWorkspaceState] = useState<WorkspaceCreationState>({
    isCreatingWorkspace: false,
  });
  
  const { user } = useAuth();
  
  const { 
    createWorkspace, 
    isLoading: isWorkspaceLoading, 
    error: workspaceError,
    clearError: clearWorkspaceError,
  } = useWorkspaceCreationBase();

  const createWorkspaceForSearch = useCallback(async (
    keyword: string, 
    languageName: string, 
    countryName: string
  ): Promise<{ workspaceId: string; workspaceName: string }> => {
    const userEmail = user?.primaryEmailAddress?.emailAddress;
    debugLog(userEmail, '[useWorkspaceCreationForSearch] Starting workspace creation for search');
    
    setWorkspaceState({ isCreatingWorkspace: false });
    
    // Clear any previous errors
    clearWorkspaceError();
    
    // Generate workspace name from search parameters
    const workspaceName = generateWorkspaceName(keyword, languageName, countryName);
    debugLog(userEmail, '[useWorkspaceCreationForSearch] Generated workspace name:', workspaceName);
    
    try {
      setWorkspaceState(prev => ({ ...prev, isCreatingWorkspace: true }));
      
      const workspaceResult = await createWorkspace({
        name: workspaceName,
        description: `Auto-generated workspace for "${keyword}" keyword research and analysis`
      });
      
      if (!workspaceResult.success || !workspaceResult.workspace) {
        throw new Error('Failed to create workspace');
      }
      
      const finalWorkspaceId = workspaceResult.workspace.id;
      const finalWorkspaceName = workspaceResult.workspace.name;
      
      debugLog(userEmail, '[useWorkspaceCreationForSearch] Workspace created successfully:', {
        id: finalWorkspaceId,
        name: finalWorkspaceName
      });
      
      // Update state with created workspace
      setWorkspaceState(prev => ({
        ...prev,
        isCreatingWorkspace: false,
        workspaceCreated: {
          id: finalWorkspaceId,
          name: finalWorkspaceName
        }
      }));
      
      return {
        workspaceId: finalWorkspaceId,
        workspaceName: finalWorkspaceName
      };
      
    } catch (workspaceCreateError) {
      console.error('[useWorkspaceCreationForSearch] Workspace creation failed:', workspaceCreateError); // Keep actual errors for all users
      
      // Set state to not creating and continue without workspace (fallback behavior)
      setWorkspaceState(prev => ({ ...prev, isCreatingWorkspace: false }));
      
      // Return fallback values
      return {
        workspaceId: '',
        workspaceName: workspaceName // Use generated name for tracking
      };
    }
  }, [createWorkspace, clearWorkspaceError, user]);

  const resetWorkspaceState = useCallback(() => {
    setWorkspaceState({
      isCreatingWorkspace: false,
    });
  }, []);

  return {
    createWorkspaceForSearch,
    workspaceState,
    resetWorkspaceState,
    isCreatingWorkspace: workspaceState.isCreatingWorkspace || isWorkspaceLoading
  };
}